import * as types from '../mutation-type/auth'
import authService from '../services/auth'
import * as session from '../../helpers/session'
import settingsService from '~/store/services/settings'

// state
export const state = {
  user: null,
  token: session.getStorage('token'),
  updateUserResponse: '',
  registerUser: '',
  sendEmailData: '',
  resendEmailData: '',
  verifyEmailData: '',
  errors: [],
  loginAs: false
}

// getters
export const getters = {
  user: state => state.user,
  token: state => state.token,
  check: state => !!state.user,
  isSuperAdmin: state => state.user && state.user.top_admin === true,
  isAdmin: state => state.user && state.user.role && state.user.role === 'admin',
  isCompanyAdmin : state => state.user && state.user.role && state.user.role === 'company_admin',
  isBuyer: state => state.user?.role === 'buyer',
  isAssociate: state => state.user?.role === 'associate',
  isRep: state => state.user?.role === 'rep',
  permissions: state => state.user?.permissions,
  showWholesalePrice: state => !state.user?.is_wholesale_price_hidden,
  uiPreferences: state => state.user?.ui_preferences || {},
  updateUserResponse: state => state.updateUserResponse,
  registerUser: state => state.registerUser,
  sendEmailData: state => state.sendEmailData,
  resendEmailData: state => state.resendEmailData,
  verifyEmailData: state => state.verifyEmailData,
  getErrors: state => state.errors,
  loginAs: state => state.loginAs
}

// mutations
export const mutations = {
  [types.SAVE_TOKEN] (state, { token, remember }) {
    state.token = token
    session.setStorage('token', token)
  },

  [types.FETCH_USER_SUCCESS] (state, { user }) {
    state.user = user
  },

  [types.FETCH_USER_FAILURE] (state) {
    state.token = null
    session.removeStorage('token')
  },

  [types.LOGOUT] (state) {
    state.user = null
    state.token = null
    session.removeStorage('token')
  },

  [types.UPDATE_USER] (state, { user }) {
    state.user = user
  },
  [types.SET_WHOLESALE_PRICE_HIDDEN] (state, hidden) {
    if (state.user) {
      state.user = { ...state.user, is_wholesale_price_hidden: hidden }
    }
  },
  [types.SET_UI_PREFERENCE] (state, { namespace, key, value }) {
    if (!state.user) return
    const preferences = { ...(state.user.ui_preferences || {}) }
    preferences[namespace] = { ...(preferences[namespace] || {}), [key]: value }
    state.user = { ...state.user, ui_preferences: preferences }
  },
  [types.UPDATE_USER_INFO] (state, payload) {
    state.updateUserResponse = payload
  },
  [types.REGISTER] (state, payload) {
    state.registerUser = payload
  },
  [types.SEND_EMAIL] (state, payload) {
    state.sendEmailData = payload
  },
  [types.RESEND_EMAIL] (state, payload) {
    state.resendEmailData = payload
  },
  [types.VERIFY_EMAIL] (state, payload) {
    state.verifyEmailData = payload
  },
  [types.UPDATE_TAGS_COUNT] (state, payload) {
    if (payload.type === 'add') {
      state.user.tags_count = state.user.tags_count + payload.count
    } else {
      state.user.tags_count = state.user.tags_count - payload.count
    }
  },
  [types.CLEAR_TAGS_COUNT] (state) {
    state.user.tags_count = 0
  },
  [types.REGISTER_WHOLESALE_ACCOUNT] (state, payload) {
    state.registerWholesaleAccount = payload
  },
  [types.SET_ERROR_STATE] (state, payload) {
    state.errors = []
    const errors = []
    if (payload.response?.status === 422) {
      for (const key in payload.response?.data?.errors) {
        errors.push(payload.response?.data?.errors[key][0])
      }
    } else if (payload.response?.status === 400 || payload.response?.status === 401) {
      errors.push(payload.response?.data?.message)
    }
    state.errors = [...errors]
  },
  [types.LOGIN_AS] (state, payload) {
    state.loginAs = payload
  }
}

// actions
export const actions = {
  saveToken ({ commit, dispatch }, payload) {
    commit(types.SAVE_TOKEN, payload)
  },

  async fetchUser ({ commit }) {
    try {
      const { data } = await authService.fetchUser()

      commit(types.FETCH_USER_SUCCESS, { user: data })
    } catch (e) {
      commit(types.FETCH_USER_FAILURE)
    }
  },

  async updateUserInfo ({ commit }, payload) {
    try {
      const response = await authService.updateUser(payload)
      commit(types.UPDATE_USER_INFO, response)
      return {
        status: 200,
        message: response?.data?.message || 'Updated successfully'
      }
    } catch (error) {
      return {
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'An error occurred'
      }
    }
  },
  async updateUser ({ commit }, payload) {
    commit(types.UPDATE_USER, payload)
  },

  // Hide wholesale prices for the current user (no password needed).
  async disableWholesalePrice ({ commit }) {
    await authService.hideWholesalePrice()
    commit(types.SET_WHOLESALE_PRICE_HIDDEN, true)
  },

  // Re-enable wholesale prices. Verifies the password server-side; on success
  // the flag is cleared. Returns { status, message } so the modal can surface
  // an incorrect-password error.
  async enableWholesalePrice ({ commit }, password) {
    try {
      const response = await authService.showWholesalePrice(password)
      commit(types.SET_WHOLESALE_PRICE_HIDDEN, false)
      return {
        status: 200,
        message: response?.data?.message || 'Wholesale prices enabled'
      }
    } catch (error) {
      return {
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'An error occurred'
      }
    }
  },

  // Persist a single namespaced UI preference for the current user. Commits
  // optimistically so the UI reflects the change instantly; the backend is the
  // source of truth on next fetchUser. Fire-and-forget — a failed save must not
  // break the interaction that triggered it.
  async saveUiPreference ({ commit }, { namespace, key, value }) {
    commit(types.SET_UI_PREFERENCE, { namespace, key, value })
    try {
      await authService.updateUiPreference({ namespace, key, value })
    } catch (e) {
      console.log('Failed to save UI preference: ', e)
    }
  },

  async logout ({ commit, dispatch }) {
    try {
      await authService.logout()
      localStorage.clear()
    } catch (e) { }

    // Reset the in-memory search-history module (the LOGOUT mutation only clears
    // user/token) and drop this user's cache — the account's history stays on the server.
    dispatch('search_history/clearLocal', null, { root: true })

    commit(types.LOGOUT)
  },

  async fetchOauthUrl (ctx, { provider }) {
    const { data } = await authService.fetchOAuthUrl(provider)

    return data.url
  },
  async updateAddress ({ commit }, { addressId, payload }) {
    try {
      await authService.updateAddress(addressId, payload)
      commit(types.UPDATE_ADDRESS)
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async addUser ({ dispatch }, data) {
    try {
      await authService.addUser(data)
      dispatch('fetchUser')
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async register ({ commit }, payload) {
    try {
      const { data } = await authService.register(payload)
      commit(types.REGISTER, data)
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async login ({ commit }, { payload, remember = false }) {
    try {
      const { data: { token } } = await authService.login(payload)
      await commit(types.SAVE_TOKEN, { token, remember })
      if (!remember) {
        //await commit(types.UPDATE_USER, { user: data })
      }
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },

  async loginAsUser ({ commit, dispatch }, payload, remember = false) {
        try {
            const { data: { token, success } } = await authService.loginAsUser({email : payload})
            await commit(types.SAVE_TOKEN, { token, remember })
            if (success) {
                await commit(types.LOGIN_AS, true)
                // Impersonation may not remount the header, so force a fresh hydrate of the
                // impersonated user's history (token-based) instead of showing the admin's.
                dispatch('search_history/clearLocal', null, { root: true })
                dispatch('search_history/hydrate', { force: true }, { root: true })
            }
        } catch (error) {
            console.log('Error: ', error)
            throw error
        }
  },

  async verifyEmail ({ commit }, { id, query }) {
    try {
      const { data } = await authService.verifyEmail(id, query)
      commit(types.VERIFY_EMAIL, data)
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async resendEmail ({ commit }, payload) {
    try {
      const { data } = await authService.resendEmail(payload)
      commit(types.RESEND_EMAIL, data)
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async sendEmail ({ commit }, payload) {
    try {
      const { data } = await authService.sendEmail(payload)
      commit(types.SEND_EMAIL, data)
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  updateTagsCount ({ commit }, payload) {
    commit(types.UPDATE_TAGS_COUNT, payload)
  },
  clearTagsCount ({ commit }) {
    commit(types.CLEAR_TAGS_COUNT)
  },
  async registerWholesaleAccount ({ commit }, payload) {
    try {
      return await authService.registerWholesaleAccount(payload)
    } catch (error) {
      commit(types.SET_ERROR_STATE, error)
      return error
    }
  },
  async associateExistingCompanyAccount ({ commit }, payload) {
    try {
      return await authService.registerWholesaleAccount(payload)
    } catch (error) {
      commit(types.SET_ERROR_STATE, error)
      return error
    }
  },
  async resetPassword ({ commit }, password) {
    try {
        const { data : {message} } = await authService.resetPassword(password)
        return message
    } catch (e) {
        console.log(e)
    }
  },
  async updatePassword ({ commit }, payload) {
    try {
      const { data } = await authService.updatePassword(payload)
      return data
    } catch (e) {
      console.log(e)
      throw e
    }
  },
}
